import { CheckIcon, ChevronDownIcon } from "@radix-ui/react-icons"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

export function ModelCombobox({
  id,
  onChange,
  options = [],
  placeholder = "Choose a model",
  value = ""
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (!open) setQuery("")
  }, [open])

  const filteredOptions = options.filter((option) => option.toLowerCase().includes(query.toLowerCase()))
  const customValue = query.trim()
  const hasCustomOption = customValue && !options.some((option) => option.toLowerCase() === customValue.toLowerCase())

  const commitValue = (nextValue) => {
    onChange(nextValue)
    setOpen(false)
  }

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-controls={id}
          aria-expanded={open}
          className="mt-2 h-12 w-full justify-between rounded-[1rem] bg-white px-4 text-left font-normal"
          type="button"
          variant="outline"
        >
          <span className={cn("truncate", !value && "text-[var(--color-text-faint)]")}>{value || placeholder}</span>
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-[var(--color-text-faint)]" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-[min(34rem,calc(100vw-2rem))] p-0">
        <div className="border-b border-[var(--color-border)] p-3">
          <input
            className="input-field h-11"
            id={id}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search or enter a model id"
            value={query}
          />
        </div>

        <div className="max-h-72 overflow-y-auto p-1">
          {hasCustomOption ? (
            <button
              className="flex w-full items-center justify-between rounded-[1rem] px-3 py-2 text-left text-sm text-[var(--color-charcoal-blue)] hover:bg-[var(--color-panel)]"
              onClick={() => commitValue(customValue)}
              type="button"
            >
              <span className="truncate">{customValue}</span>
              <span className="ml-3 shrink-0 text-xs text-[var(--color-text-faint)]">Custom</span>
            </button>
          ) : null}

          {filteredOptions.length > 0 ? filteredOptions.map((option) => (
            <button
              className="flex w-full items-center justify-between rounded-[1rem] px-3 py-2 text-left text-sm text-[var(--color-charcoal-blue)] hover:bg-[var(--color-panel)]"
              key={option}
              onClick={() => commitValue(option)}
              type="button"
            >
              <span className="truncate">{option}</span>
              <CheckIcon className={cn("ml-3 h-4 w-4 shrink-0", value === option ? "opacity-100" : "opacity-0")} />
            </button>
          )) : (
            <p className="px-3 py-4 text-sm text-[var(--color-text-faint)]">No matching models</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
